import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { StatusBadge, DonorTypeBadge } from "@/components/StatusBadge";
import { EUR, fmtDate, donorName } from "@/lib/format";
import { DONORS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DonorFormDialog from "@/components/DonorFormDialog";
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Eye, Download } from "lucide-react";
import { toast } from "sonner";

export default function Donors() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const [createOpen, setCreateOpen] = useState(false);
  const [editDonor, setEditDonor] = useState(null);
  const [deleteDonor, setDeleteDonor] = useState(null);

  // reset page quando cambia un filtro
  React.useEffect(() => { setPage(0); }, [search, typeFilter, statusFilter]);

  const { data: result, isLoading } = useQuery({
    queryKey: ["donors", { search, typeFilter, statusFilter, page }],
    queryFn: async () => {
      const params = { paginated: true, limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (search) params.search = search;
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      return (await api.get("/donors", { params })).data;
    },
    keepPreviousData: true,
  });
  const donors = result?.items || [];
  const total = result?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [exporting, setExporting] = useState(false);
  async function exportCsv() {
    setExporting(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/donors/export/csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `donatori_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Esportazione non riuscita");
    } finally {
      setExporting(false);
    }
  }

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post("/donors", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["donors"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Donatore creato");
      setCreateOpen(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const editMut = useMutation({
    mutationFn: async ({ id, payload }) => (await api.patch(`/donors/${id}`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["donors"] });
      qc.invalidateQueries({ queryKey: ["donor"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Donatore aggiornato");
      setEditDonor(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/donors/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["donors"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Donatore eliminato");
      setDeleteDonor(null);
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
      setDeleteDonor(null);
    },
  });

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Donatori"
        subtitle="Anagrafica completa con stato relazione, storico pagamenti e comunicazioni."
        actions={
          <div className="flex gap-2">
          <Button
            data-testid="donors-export-csv-btn"
            variant="outline"
            disabled={exporting}
            onClick={exportCsv}
            className="rounded-full"
          >
            <Download size={16} className="mr-1.5" /> {exporting ? "Esportazione…" : "Esporta CSV"}
          </Button>
          <Button
            data-testid={DONORS.addBtn}
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150"
          >
            <Plus size={16} className="mr-1.5" /> Nuovo donatore
          </Button>
          </div>
        }
      />

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row md:items-center gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              data-testid={DONORS.searchInput}
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, email, organizzazione…"
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid={DONORS.typeFilter} className="w-40"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="privato">Privati</SelectItem>
              <SelectItem value="chiesa">Chiese</SelectItem>
              <SelectItem value="azienda">Aziende</SelectItem>
              <SelectItem value="associazione">Associazioni</SelectItem>
              <SelectItem value="fondazione">Fondazioni</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="regolare">Regolare</SelectItem>
              <SelectItem value="ritardo">In ritardo</SelectItem>
              <SelectItem value="fermo">Fermo</SelectItem>
              <SelectItem value="da_ricontattare">Da ricontattare</SelectItem>
              <SelectItem value="occasionale">Occasionale</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50/60 hover:bg-stone-50/60">
                <TableHead className="uppercase text-[10px] tracking-widest">Donatore</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Tipo</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Stato</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest text-right">Impegno annuo</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest text-right">Totale</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Ultimo pagamento</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest">Contatti</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-stone-400">Caricamento…</TableCell></TableRow>
              )}
              {!isLoading && donors.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-stone-400">Nessun donatore trovato.</TableCell></TableRow>
              )}
              {donors.map((d, i) => (
                <TableRow
                  key={d.id}
                  data-testid={DONORS.row(d.id)}
                  className="animate-in-row hover:bg-stone-50/50"
                  style={{ animationDelay: `${i * 12}ms` }}
                >
                  <TableCell>
                    <Link to={`/donatori/${d.id}`} className="font-medium hover:text-[#1C352D]">
                      {donorName(d)}
                    </Link>
                    <div className="text-[11px] text-stone-500">{d.city || "—"}{d.referente ? ` · ${d.referente}` : ""}</div>
                  </TableCell>
                  <TableCell><DonorTypeBadge type={d.type} /></TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell className="text-right">
                    {d.annual_pledged_amount > 0 ? (
                      <div>
                        <div className="font-semibold text-[#1C352D]">{EUR.format(d.annual_pledged_amount)}</div>
                        <div className="text-[10px] text-stone-500">{d.active_pledges_count} impegn{d.active_pledges_count === 1 ? "o" : "i"}</div>
                      </div>
                    ) : (
                      <span className="text-stone-300 text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold">{EUR.format(d.total_donated || 0)}</TableCell>
                  <TableCell className="text-sm">{fmtDate(d.last_payment_date)}</TableCell>
                  <TableCell className="text-xs text-stone-500">
                    <div>{d.email || "—"}</div>
                    <div>{d.phone || "—"}</div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`donor-actions-${d.id}`}
                          className="p-1.5 rounded-md hover:bg-stone-100 text-stone-500 hover:text-[#1C352D] transition-colors duration-150"
                        >
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40 bg-white">
                        <DropdownMenuItem
                          data-testid={`donor-view-${d.id}`}
                          onClick={() => navigate(`/donatori/${d.id}`)}
                        >
                          <Eye size={14} className="mr-2"/> Apri scheda
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={`donor-edit-${d.id}`}
                          onClick={() => setEditDonor(d)}
                        >
                          <Pencil size={14} className="mr-2"/> Modifica
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`donor-delete-${d.id}`}
                          className="text-red-700 focus:text-red-800 focus:bg-red-50"
                          onClick={() => setDeleteDonor(d)}
                        >
                          <Trash2 size={14} className="mr-2"/> Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <DonorFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        submitting={createMut.isPending}
        onSubmit={(payload) => createMut.mutate(payload)}
      />

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-stone-600" data-testid="donors-pagination">
          <div>
            Pagina <b>{page + 1}</b> di <b>{totalPages}</b> · {total} donatori totali
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              data-testid="donors-page-prev"
            >
              ← Precedente
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              data-testid="donors-page-next"
            >
              Successiva →
            </Button>
          </div>
        </div>
      )}

      <DonorFormDialog
        open={!!editDonor}
        onOpenChange={(v) => { if (!v) setEditDonor(null); }}
        mode="edit"
        initialDonor={editDonor}
        submitting={editMut.isPending}
        onSubmit={(payload) => editMut.mutate({ id: editDonor.id, payload })}
      />

      <AlertDialog open={!!deleteDonor} onOpenChange={(v) => { if (!v) setDeleteDonor(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {deleteDonor ? donorName(deleteDonor) : "questo donatore"}?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;operazione è irreversibile: verranno rimossi anche impegni, scadenze, pagamenti,
              comunicazioni e follow-up collegati. I documenti caricati resteranno archiviati come cancellati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="donor-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="donor-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => deleteDonor && deleteMut.mutate(deleteDonor.id)}
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
